import React, { useState } from "react";
import NavBar from "../components/nav-bar/Nav";
import SideBar from "../components/Sidebar";
import PageHeader from "../components/layout/PageHeader";
import EmailGrab from "../components/Hero/EmailGrab";
import { Band, BandInner } from "../components/Hero/HeroElements";
import { Helmet } from "react-helmet-async";
import { Team } from "../data/HeroSpotlight";
import Portrait from "../components/layout/Portrait";
import styled from "styled-components";
import { color, font } from "../theme/tokens";

/**
 * The container carries the right/bottom rules as well as top/left. A partial
 * final row then still closes against the container edge instead of stopping
 * short, at any column count.
 */
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  border: 1px solid ${color.rule};

  @media (max-width: 1000px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 620px) { grid-template-columns: 1fr; }
`;

const Card = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  background: transparent;
  border: none;
  border-right: 1px solid ${color.rule};
  border-bottom: 1px solid ${color.rule};
  padding: 32px 28px 36px;
  cursor: pointer;
  font: inherit;
  transition: background 200ms ease;

  &:hover { background: ${color.greenTint}; }
`;

/* Square portraits, not circles — reads as a masthead rather than a profile row. */
const Avatar = styled(Portrait)`
  img { filter: grayscale(1); transition: filter 240ms ease; }

  ${Card}:hover & img { filter: grayscale(0); }

  /* At one column a full-width square portrait is ~340px tall and dominates
     the card; cap it so the page stays scannable on a phone. */
  @media (max-width: 620px) {
    max-width: 168px;
  }
`;

/* Portraits in the modal keep a fixed size instead of filling the column. */
const ModalAvatar = styled(Portrait)`
  width: 104px;
  flex: 0 0 104px;
`;


const Name = styled.h3`
  font-family: ${font.display};
  color: ${color.inkStrong};
  font-size: 22px;
  font-weight: 500;
  letter-spacing: -0.01em;
  margin: 22px 0 6px;
`;

const Role = styled.div`
  font-family: ${font.body};
  color: ${color.inkMuted};
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.14em;
`;

const Bio = styled.p`
  font-family: ${font.body};
  color: ${color.ink};
  font-size: 15px;
  line-height: 1.7;
  margin: 16px 0 0;
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(20, 20, 20, 0.45);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const Modal = styled.div`
  background: ${color.paper};
  width: min(680px, 100%);
  max-height: 86vh;
  overflow: auto;
  padding: 40px;

  @media (max-width: 600px) { padding: 28px 22px; }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 20px;
`;

const CloseBtn = styled.button`
  margin-left: auto;
  border: none;
  background: none;
  padding: 4px;
  cursor: pointer;
  font-family: ${font.body};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${color.inkMuted};

  &:hover { color: ${color.clay}; }
`;

export default function TeamPage() {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const toggle = () => setIsOpen(!isOpen);

  return (
    <>
      <Helmet>
        <title>Team | Vitalforce</title>
      </Helmet>

      <SideBar $isopen={isOpen} toggle={toggle} />
      <NavBar toggle={toggle} />

      <PageHeader
        eyebrow="People"
        title="Operators, not spectators."
        lede="A team of founders and CPG operators who have built, scaled and sold consumer brands — partnering with the people doing it next."
      />

      <Band>
        <BandInner>
          <Grid>
            {Team.map((member, i) => (
              <Card key={i} onClick={() => setSelected(member)}>
                <Avatar src={member.imageUrl} name={member.name} />
                <Name>{member.name}</Name>
                <Role>{member.position}</Role>
                {member.bio && <Bio>{member.bio}</Bio>}
              </Card>
            ))}
          </Grid>
        </BandInner>
      </Band>

      {selected && (
        <Overlay onClick={() => setSelected(null)}>
          <Modal onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <ModalHeader>
              <ModalAvatar src={selected.imageUrl} name={selected.name} />
              <div>
                <Name style={{ marginTop: 0 }}>{selected.name}</Name>
                <Role>{selected.position}</Role>
              </div>
              <CloseBtn onClick={() => setSelected(null)}>Close</CloseBtn>
            </ModalHeader>
            {selected.bio && <Bio>{selected.bio}</Bio>}
          </Modal>
        </Overlay>
      )}

      <EmailGrab />
    </>
  );
}
